import { useState, useMemo } from 'react'
import { sortJobsAccordingToPrimary } from '../utils'

/**
 * Manages the source (Platform) and target (FactSet) profile ID inputs,
 * and resolves the matching person and job records from the merge preview response.
 * Clears any existing query errors when the user changes either ID.
 *
 * @param {Array|undefined} responseData - Array of person records returned by the merge preview API
 * @param {Object} options
 * @param {boolean} options.isError - Whether the preview query is in an error state
 * @param {Function} options.reset - Resets the preview query state (clears stale errors when IDs change)
 */
function useMergeSelections(responseData, { isError, reset }) {
  const [sourceId, setSourceId] = useState('')
  const [targetId, setTargetId] = useState('')

  const handleSourceIdChange = value => {
    setSourceId(value)
    if (isError) {
      reset()
    }
  }

  const handleTargetIdChange = value => {
    setTargetId(value)
    if (isError) {
      reset()
    }
  }

  const { sourcePerson, targetPerson, sourceJobs, targetJobs } = useMemo(() => {
    let sourcePersonDefault = null
    let targetPersonDefault = null

    responseData?.forEach(person => {
      if (person.id === sourceId) {
        sourcePersonDefault = {
          ...person,
          jobs: sortJobsAccordingToPrimary(person.jobs),
        }
      }
      if (person.id === targetId) {
        targetPersonDefault = {
          ...person,
          jobs: sortJobsAccordingToPrimary(person.jobs),
        }
      }
    })

    return {
      sourcePerson: sourcePersonDefault,
      targetPerson: targetPersonDefault,
      sourceJobs: sourcePersonDefault?.jobs || [],
      targetJobs: targetPersonDefault?.jobs || [],
    }
  }, [responseData, sourceId, targetId])

  return {
    sourceId,
    targetId,
    handleSourceIdChange,
    handleTargetIdChange,
    sourcePerson,
    targetPerson,
    sourceJobs,
    targetJobs,
  }
}

export default useMergeSelections
